import { t, tf, tx, txItems, type TextItem } from '../i18n';

/**
 * Names that arrive as data in English (seeded rows, free labels). Translate them here by their
 * stable key, once per kind, so every page shows the same word. Unknown values (a custom role, a
 * label the broker typed) stay as sent.
 */

const RULES: Record<string, () => string> = {
  latency_arbitrage: () => t('app.rule.latency-arbitrage', 'Latency arbitrage'),
  scalping_sub30s: () => t('app.rule.scalping-sub30s', 'Scalping < 30 s'),
  news_trading: () => t('app.rule.news-trading', 'News trading'),
  hedging_cross_account: () => t('app.rule.hedging-cross-account', 'Cross-account hedging'),
  coordinated_entries: () => t('app.rule.coordinated-entries', 'Coordinated entries'),
  martingale: () => t('app.rule.martingale', 'Martingale'),
  grid: () => t('app.rule.grid', 'Grid'),
  swap_free_carry: () => t('app.rule.swap-free-carry', 'Swap-free carry'),
  bonus_credit_abuse: () => t('app.rule.bonus-credit-abuse', 'Bonus / credit abuse'),
  ea_share: () => t('app.rule.ea-share', 'Expert advisor share'),
};

/** Name of a detection rule by key (catalogue of backend/internal/classify/rules.go). */
export function ruleName(key: string, fallback?: string | null): string {
  return RULES[key]?.() ?? fallback ?? key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Name on a classification badge: its catalog key, or the rule's name for a row written without one. */
export function badgeName(b: { key: string; label: string; label_key?: string | null }): string {
  return b.label_key ? tf(b, 'label') : ruleName(b.key, b.label);
}

const ROLES: Record<string, () => string> = {
  owner: () => t('app.role.owner', 'Owner'),
  risk_manager: () => t('app.role.risk-manager', 'Risk manager'),
  dealer: () => t('app.role.dealer', 'Dealer'),
  compliance: () => t('app.role.compliance', 'Compliance'),
  executive: () => t('app.role.executive', 'Executive'),
  read_only: () => t('app.role.read-only', 'Read-only'),
  launcher: () => t('app.role.launcher', 'Launcher'),
};
/** The presets by their English name, for API fields that send only the name. */
const ROLE_BY_NAME: Record<string, string> = {
  Owner: 'owner',
  'Risk manager': 'risk_manager',
  Dealer: 'dealer',
  Compliance: 'compliance',
  Executive: 'executive',
  'Read-only': 'read_only',
  Launcher: 'launcher',
};

/**
 * Name of a role: the preset's translation, or the name its creator typed for a custom role. Takes
 * the role object or, where the API sends only that, its name or preset key.
 */
export function roleName(role: { key?: string | null; name: string } | string | null | undefined): string {
  if (!role) return '';
  if (typeof role === 'string' && ROLES[role]) return ROLES[role]();
  const r = typeof role === 'string' ? { name: role } : role;
  const key = ROLE_BY_NAME[r.name];
  // a renamed preset keeps the name the broker gave it, and so does a custom role called "Owner"
  return key && (!('key' in r) || !r.key || r.key === key) ? ROLES[key]() : r.name;
}

const ACCOUNT_TYPES: Record<string, () => string> = {
  Standard: () => t('app.account-type.standard', 'Standard'),
  Pro: () => t('app.account-type.pro', 'Pro'),
  Raw: () => t('app.account-type.raw', 'Raw'),
  Cent: () => t('app.account-type.cent', 'Cent'),
  Contest: () => t('app.account-type.contest', 'Contest'),
  Demo: () => t('app.account-type.demo', 'Demo'),
  'Swap-free': () => t('app.account-type.swap-free', 'Swap-free'),
};

/** Account type of a group: a free label on the mapping page; the usual ones are translated. */
export function accountTypeName(v: string | null | undefined): string {
  return v ? (ACCOUNT_TYPES[v]?.() ?? v) : '';
}

/**
 * Message of an alert. A toxic alert's comes as lines of a rule and its reason (`message_items`, no
 * `message_key`): parts joined with `: `, lines with `; `.
 */
export function alertMessage(a: { message: string; message_key?: string | null; message_items?: TextItem[][] | null }): string {
  return a.message_items?.length ? a.message_items.map((line) => txItems(line, ': ')).join('; ') : tf(a, 'message');
}

/**
 * Text joined from parts with more than one separator (`ServiceHealth.detail_items` mixes ` · ` and
 * `; `). The parts come in order, so the joined English gives the separators back; parts that do not
 * match it leave the English.
 */
export function joinedText(english: string | null | undefined, items: TextItem[]): string {
  const en = english ?? '';
  let out = '';
  let at = 0;
  for (const it of items) {
    const i = en.indexOf(it.text, at);
    if (i < 0) return en;
    out += en.slice(at, i) + tx(it.text, it.key, it.params);
    at = i + it.text.length;
  }
  return out + en.slice(at);
}
